(() => {
  if (customElements.get('product-dimensions')) return;

  class ProductDimensions extends HTMLElement {
    static instances = new Set();
    static listenersAttached = false;

    static attachGlobalListeners() {
      if (this.listenersAttached) return;
      this.listenersAttached = true;

      const refreshInstances = (event) => {
        if (event?.type !== 'popstate') {
          const target = event?.target;
          if (!target) return;

          const relevantTrigger = target.closest(
            [
              'variant-selects',
              'variant-radios',
              'select[name^="options"]',
              'fieldset input[type="radio"]',
              'form[action*="/cart/add"] input[name="id"]',
              '[data-variant-id]'
            ].join(', ')
          );

          if (!relevantTrigger) return;
        }

        ProductDimensions.instances.forEach((instance) => {
          if (!instance.isConnected) return;

          const scope = instance.getScope();
          if (event?.target && scope && scope !== document && !scope.contains(event.target)) return;

          const forcedVariantId =
            event?.target?.closest?.('[data-variant-id]')?.dataset?.variantId || null;

          instance.queueRender(forcedVariantId);
        });
      };

      document.addEventListener('change', refreshInstances);
      document.addEventListener('click', refreshInstances);
      window.addEventListener('popstate', refreshInstances);
    }

    connectedCallback() {
      ProductDimensions.instances.add(this);
      ProductDimensions.attachGlobalListeners();
      this.render();
    }

    disconnectedCallback() {
      ProductDimensions.instances.delete(this);
    }

    getScope() {
      return (
        this.closest(
          [
            'product-info',
            '.product',
            '.product__info-wrapper',
            '.product-card-wrapper',
            '.card-wrapper',
            '.card',
            '.quick-add-modal__content'
          ].join(', ')
        ) || document
      );
    }

    queueRender(forcedVariantId = null) {
      [0, 100, 250].forEach((delay) => {
        window.setTimeout(() => this.render(forcedVariantId), delay);
      });
    }

    getCurrentVariantId() {
      const scope = this.getScope();

      const formVariantInput = scope.querySelector(
        'form[action*="/cart/add"] input[name="id"]'
      );
      if (formVariantInput?.value) return String(formVariantInput.value);

      const activeDataVariantEl = scope.querySelector(
        [
          '[data-variant-id][aria-checked="true"]',
          '[data-variant-id].is-active',
          '[data-variant-id]:checked',
          'option[data-variant-id]:checked'
        ].join(', ')
      );
      if (activeDataVariantEl?.dataset?.variantId) {
        return String(activeDataVariantEl.dataset.variantId);
      }

      const url = new URL(window.location.href);
      const urlVariantId = url.searchParams.get('variant');
      if (urlVariantId) return String(urlVariantId);

      return this.dataset.initialVariantId || '';
    }

    getVariantDataEl(variantId) {
      if (!variantId) return null;

      return Array.from(this.querySelectorAll('[data-variant-dimension]')).find(
        (el) => String(el.dataset.variantId) === String(variantId)
      );
    }

    getDataValue(el, key) {
      if (!el) return '';
      const value = el.dataset[key];
      return typeof value === 'string' ? value.trim() : '';
    }

    updateRow(type, value) {
      const row = this.querySelector(`[data-dimension-row="${type}"]`);
      const valueEl = this.querySelector(`[data-dimension-value="${type}"]`);

      if (!row || !valueEl) return;

      if (value) {
        valueEl.textContent = value;
        row.hidden = false;
      } else {
        valueEl.textContent = '';
        row.hidden = true;
      }
    }

    render(forcedVariantId = null) {
      const variantId = forcedVariantId || this.getCurrentVariantId();
      const variantDataEl = this.getVariantDataEl(variantId);

      const internal =
        this.getDataValue(variantDataEl, 'internal') ||
        (this.dataset.defaultInternal || '').trim();

      const external =
        this.getDataValue(variantDataEl, 'external') ||
        (this.dataset.defaultExternal || '').trim();

      this.updateRow('internal', internal);
      this.updateRow('external', external);

      this.hidden = !internal && !external;
    }
  }

  customElements.define('product-dimensions', ProductDimensions);
})();